/**
 * Sign-in page. Renders Google's official Sign-In button via the Google
 * Identity Services script loaded from accounts.google.com/gsi/client.
 *
 * Flow:
 *   1. GIS script callback fires with a signed ID token
 *   2. We POST it to /auth/sign-in with provider="google"
 *   3. Backend verifies signature against Google's JWKS + checks audience
 *   4. Backend returns our access + refresh tokens
 *   5. AuthState transitions to signedIn, router redirects to /
 *
 * Apple Sign in with Apple JS will be added after the paid Apple
 * Developer Program is active.
 */

import { useEffect, useRef, useState } from "react";

import { signIn, AuthError } from "@/features/auth/lib/authClient";
import { useAuth } from "@/features/auth/lib/authContext";
import { config } from "@/shared/config";
import { LogoMark } from "@/shared/components/LogoMark";
import { SIGN_IN_TAGLINES } from "@/features/auth/lib/signInTaglines";

export function SignIn() {
  const { setSignedIn, signInError, setSignInError } = useAuth();
  const buttonHostRef = useRef<HTMLDivElement | null>(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  // Wait for the GIS script (loaded async from index.html) to attach
  // window.google. Poll briefly; in practice it's ready within a few hundred ms.
  useEffect(() => {
    if (window.google?.accounts?.id) {
      setScriptReady(true);
      return;
    }
    const start = Date.now();
    const timer = setInterval(() => {
      if (window.google?.accounts?.id) {
        setScriptReady(true);
        clearInterval(timer);
      } else if (Date.now() - start > 10_000) {
        setSignInError(
          "Google sign-in script failed to load. Check your connection and refresh.",
        );
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [setSignInError]);

  useEffect(() => {
    if (!scriptReady || !buttonHostRef.current) return;
    if (!config.googleClientIdWeb) {
      setSignInError(
        "Google Web Client ID is missing. Set VITE_GOOGLE_CLIENT_ID_WEB at build time.",
      );
      return;
    }

    window.google!.accounts.id.initialize({
      client_id: config.googleClientIdWeb,
      callback: async (response) => {
        setIsSigningIn(true);
        // Log the raw callback shape so we can spot empty/missing credentials.
        // eslint-disable-next-line no-console
        console.log("[GSI callback]", { hasCredential: !!response?.credential, select_by: response?.select_by });
        if (!response?.credential) {
          setSignInError(
            "Google didn't return a credential (likely a COOP / popup-blocker issue: check the browser console).",
          );
          setIsSigningIn(false);
          return;
        }
        try {
          const tokens = await signIn({
            provider: "google",
            credential: response.credential,
          });
          setSignedIn(tokens);
        } catch (err) {
          // Surface the REAL error so we don't lose diagnostic info.
          // eslint-disable-next-line no-console
          console.error("[Google sign-in] failed", err);
          let message: string;
          if (err instanceof AuthError) {
            message = err.message;
          } else if (err instanceof Error) {
            message = `${err.name}: ${err.message}`;
          } else {
            message = String(err);
          }
          setSignInError(`Google sign-in failed: ${message}`);
        } finally {
          setIsSigningIn(false);
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    // Google Identity Services sizes its button to its CONTENT unless an
    // explicit `width` is passed — without it the button comes out narrower
    // than the full-width Apple button below. Pass the host's measured width
    // (capped by `max-w-[280px]`) so both buttons line up. Falls back to 280
    // if measurement isn't ready.
    // `outline` (light button) instead of `filled_black` so the one CTA
    // pops against the near-black page — a dark pill on a dark bg made
    // cold traffic hunt for the action. We can't restyle GIS's rendered
    // button directly, so the theme is the lever.
    window.google!.accounts.id.renderButton(buttonHostRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
      shape: "rectangular",
      logo_alignment: "left",
      width: buttonHostRef.current.offsetWidth || 280,
    });
  }, [scriptReady, setSignInError, setSignedIn]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      {/* Ambient brand glow — three pooled radials (amber behind the
          logo, fainter amber behind the card, indigo ambient) so the
          whole column from logo through CTA sits in one pool of light
          instead of floating in a void. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(38% 30% at 50% 34%, rgba(229,169,60,0.12), transparent 66%), radial-gradient(34% 26% at 50% 56%, rgba(229,169,60,0.06), transparent 70%), radial-gradient(50% 44% at 50% 48%, rgba(122,136,200,0.05), transparent 72%)",
        }}
      />
      <div className="login-arrive relative w-full max-w-sm">
        <header className="text-center mb-12">
          <BrandmarkHero />
          <h1 className="text-3xl font-semibold text-text mb-2 tracking-tight">
            Holdem Drills
          </h1>
          <p className="text-text-muted text-sm">
            Drill spots. See your leaks. Get better.
          </p>
        </header>

        <div className="space-y-3 flex flex-col items-center">
          {/* Google's button is rendered into this host by GIS. The wrapper
              hover lift gives our one money CTA a touch of premium polish. */}
          <div
            ref={buttonHostRef}
            className="w-full max-w-[280px] min-h-[44px] transition duration-150 hover:-translate-y-0.5 hover:drop-shadow-lg"
          />

          {(!scriptReady || isSigningIn) && (
            <p className="text-xs text-text-muted">
              {isSigningIn ? "Signing you in…" : "Loading sign-in…"}
            </p>
          )}

          {/* Apple sign-in is planned (pending Apple Developer Program).
              A muted line signals it's coming without a dead, full-size
              button competing with the working Google CTA. */}
          <p className="text-xs text-text-muted/60">Apple sign-in coming soon</p>
        </div>

        {signInError && (
          <p className="mt-4 text-sm text-danger text-center">{signInError}</p>
        )}

        <SignInTagline />
      </div>
    </div>
  );
}

/**
 * Sign-in tagline — starts on a random line from the tested
 * `SIGN_IN_TAGLINES` bank and slow-crossfades to the next every 7s so
 * the page feels alive while the user decides. Rotation is disabled
 * under prefers-reduced-motion (stays on the initial random pick).
 * Deliberately NOT in an aria-live region — a screen reader announcing
 * a rotating joke mid-task is hostile; it's plain decorative text.
 */
function SignInTagline() {
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [index, setIndex] = useState(
    () => Math.floor(Math.random() * SIGN_IN_TAGLINES.length),
  );
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (reduced) return;
    let fade: ReturnType<typeof setTimeout>;
    const id = setInterval(() => {
      setShow(false); // fade out
      fade = setTimeout(() => {
        setIndex((p) => (p + 1) % SIGN_IN_TAGLINES.length);
        setShow(true); // fade in the next line
      }, 350);
    }, 7000);
    return () => {
      clearInterval(id);
      clearTimeout(fade);
    };
  }, [reduced]);

  return (
    <p
      className="mt-8 text-center text-xs text-text-muted/70"
      style={{ opacity: show ? 1 : 0, transition: "opacity 350ms ease" }}
    >
      {SIGN_IN_TAGLINES[index]}
    </p>
  );
}

/**
 * Hero brand mark for the sign-in screen — the `LogoMark` crosshair at
 * 64px with a soft amber outer glow. The draw-in + pulse animation plays
 * on mount, giving the sign-in splash a little life.
 */
function BrandmarkHero() {
  return (
    <div
      className="mx-auto mb-5 w-16 h-16 flex items-center justify-center"
      style={{ filter: "drop-shadow(0 0 24px rgba(229,169,60,0.5))" }}
    >
      <LogoMark size={64} />
    </div>
  );
}
